import { createTheme } from "@mui/material/styles";

// Colors that differ between the light and dark themes; everything else is shared.
const modePalettes = {
	light: {
		primary: {
			main: "#4f46e5",
			light: "#6366f1",
			dark: "#3730a3",
			contrastText: "#ffffff",
		},
		background: {
			default: "#f8fafc",
			paper: "#ffffff",
		},
		text: {
			primary: "#0f172a",
			secondary: "#64748b",
			disabled: "#cbd5e1",
		},
		divider: "#e2e8f0",
	},
	dark: {
		primary: {
			main: "#6366f1",
			light: "#818cf8",
			dark: "#4f46e5",
			contrastText: "#ffffff",
		},
		background: {
			default: "#0b0d17",
			paper: "#141724",
		},
		text: {
			primary: "#e6e8f2",
			secondary: "#9ba1b9",
			disabled: "#4b5068",
		},
		divider: "rgba(255, 255, 255, 0.09)",
	},
};

// Modern color palette inspired by top SaaS applications
export const getTheme = (mode) => createTheme({
	palette: {
		mode,
		...modePalettes[mode],
		secondary: {
			main: "#ec4899", // Pink
			light: "#f472b6",
			dark: "#db2777",
			contrastText: "#ffffff",
		},
		success: {
			main: "#10b981",
			light: "#34d399",
			dark: "#059669",
		},
		error: {
			main: "#ef4444",
			light: "#f87171",
			dark: "#dc2626",
		},
		warning: {
			main: "#f59e0b",
			light: "#fbbf24",
			dark: "#d97706",
		},
		info: {
			main: "#3b82f6",
			light: "#60a5fa",
			dark: "#2563eb",
		},
	},
	typography: {
		fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
		h1: {
			fontSize: "2.5rem",
			fontWeight: 800,
			lineHeight: 1.2,
			letterSpacing: "-0.02em",
		},
		h2: {
			fontSize: "2rem",
			fontWeight: 700,
			lineHeight: 1.3,
			letterSpacing: "-0.01em",
		},
		h3: {
			fontSize: "1.75rem",
			fontWeight: 700,
			lineHeight: 1.4,
		},
		h4: {
			fontSize: "1.5rem",
			fontWeight: 600,
			lineHeight: 1.4,
		},
		h5: {
			fontSize: "1.25rem",
			fontWeight: 600,
			lineHeight: 1.5,
		},
		h6: {
			fontSize: "1rem",
			fontWeight: 600,
			lineHeight: 1.5,
		},
		subtitle1: {
			fontSize: "1rem",
			fontWeight: 500,
			lineHeight: 1.75,
		},
		subtitle2: {
			fontSize: "0.875rem",
			fontWeight: 500,
			lineHeight: 1.57,
		},
		body1: {
			fontSize: "1rem",
			lineHeight: 1.75,
		},
		body2: {
			fontSize: "0.875rem",
			lineHeight: 1.57,
		},
		button: {
			fontSize: "0.875rem",
			fontWeight: 600,
			textTransform: "none",
			letterSpacing: "0.02em",
		},
	},
	shape: {
		borderRadius: 12,
	},
	shadows: [
		"none",
		"0 1px 2px 0 rgb(0 0 0 / 0.05)",
		"0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
		"0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
		"0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
		"0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
		"0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 0 1px rgb(0 0 0 / 0.05), 0 20px 25px -5px rgb(0 0 0 / 0.1)",
		"0 0 0 1px rgb(0 0 0 / 0.05), 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
		"0 0 #0000, 0 0 #0000, 0 25px 50px -12px rgb(0 0 0 / 0.25)",
	],
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: 10,
					padding: "10px 24px",
					fontSize: "0.875rem",
					fontWeight: 600,
					boxShadow: "none",
					transition: "all 0.2s ease-in-out",
					"&:hover": {
						transform: "translateY(-1px)",
						boxShadow: "0 4px 12px rgba(79, 70, 229, 0.3)",
					},
				},
				contained: {
					"&:hover": {
						boxShadow: "0 4px 12px rgba(79, 70, 229, 0.4)",
					},
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					borderRadius: 16,
					boxShadow:
						"0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
					transition: "all 0.3s ease-in-out",
					"&:hover": {
						boxShadow:
							"0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
						transform: "translateY(-2px)",
					},
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-root": {
						borderRadius: 10,
						transition: "all 0.2s ease-in-out",
						"&:hover": {
							boxShadow: "0 0 0 2px rgba(79, 70, 229, 0.1)",
						},
						"&.Mui-focused": {
							boxShadow: "0 0 0 3px rgba(79, 70, 229, 0.15)",
						},
					},
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					fontWeight: 500,
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					borderRadius: 12,
					// MUI lightens dark-mode paper with a gradient per elevation; keep surfaces flat.
					backgroundImage: "none",
				},
				elevation1: {
					boxShadow:
						"0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
				},
			},
		},
		MuiAppBar: {
			styleOverrides: {
				root: {
					boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
					backdropFilter: "blur(8px)",
					backgroundColor: mode === "dark" ? "rgba(20, 23, 36, 0.8)" : "rgba(255, 255, 255, 0.8)",
				},
			},
		},
	},
});

